import React from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '@desygner/ui-common-components';
import useHoverEdgeDetection from '../../hooks/useHoverEdgeDetection';

export const cellStyle = css`
  position: relative;
  border-color: ${({ theme }) => theme.Color.Neutrals.Text};
  border-style: solid;
  border-width: 0;
  background: ${({ theme }) => theme.Color.Neutrals.Light};
  transition: ${({ theme }) => `all ${theme.Transition.Timing.out}`};
  cursor: pointer;

  &:hover {
    transition: ${({ theme }) => `all ${theme.Transition.Timing.in}`};
    background: ${({ theme }) => theme.Color.System.Primary};
    border-color: ${({ theme }) => theme.Color.System.Primary};
  }

  ${({ isActive, theme }) => isActive && css`
    background: ${theme.Color.System.Primary};
    border-color: ${theme.Color.System.Primary};
  `}
`;

export const Wrapper = styled.div`
  display: flex;
  position: relative;
  overflow: visible;
  ${({ width, height, gripSize, gutterSize }) => css`
    width: ${width}px;
    height: ${height}px;
    top: ${gutterSize - gripSize}px;
    left: ${gutterSize - gripSize}px;
  `}

  .selected & {
    display: flex;
  }
`;

export const Corner = styled.div`
  ${cellStyle}
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ gripSize }) => gripSize}px;
  height: ${({ gripSize }) => gripSize}px;
  border-radius: ${({ gripSize }) => gripSize / 2.5}px 0 0 0;

  border-left-width: ${({ borderWidth }) => borderWidth}px;
  border-top-width: ${({ borderWidth }) => borderWidth}px;
`;

const IconWrapper = styled.div`
  position: absolute;
  ${({ gripSize }) => css`
    width: ${gripSize * 2}px;
    height: ${gripSize * 2}px;
    border-radius: ${gripSize / 1.65}px;
  `}
  top: 50%;
  left: 50%;
  background: ${({ theme }) => theme.Color.Neutrals.Paper};
  box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.05);
  color: ${({ theme }) => theme.Color.Neutrals.Light};
  cursor: pointer;
  transition: ${({ theme }) => `all ${theme.Transition.Timing.out}`};

  i, svg {
    display: block;
    ${({ gripSize }) => css`
    width: ${gripSize * 2}px;
    height: ${gripSize * 2}px;
  `}

  svg {
    fill: ${({ theme }) => theme.Color.Neutrals.Dark};
    transition: ${({ theme }) => `all ${theme.Transition.Timing.out}`};
  }
`;
const CloseIconWrapper = styled(IconWrapper)`
  transform: translate(-50%, -50%);
  &:hover {
    background: ${({ theme }) => theme.Color.Feedback.Alert};
    svg {
      fill: ${({ theme }) => theme.Color.Neutrals.Paper};
      transition: ${({ theme }) => `all ${theme.Transition.Timing.in}`}
    }
  }
`;
const AddIconWrapper = styled(IconWrapper)`
  ${({ type, gripSize, position }) => css`
    ${type === 'row' && position === 'before' && css`
      top: -${gripSize}px; bottom: auto;
    `}
    ${type === 'row' && position === 'after' && css`
      top: auto; bottom: -${gripSize}px;
    `}
    ${type === 'column' && position === 'before' && css`
      left: -${gripSize}px; right: auto;
    `}
    ${type === 'column' && position === 'after' && css`
      left: auto; right: -${gripSize}px;
    `}
  `}

  &:hover {
    transition: ${({ theme }) => `all ${theme.Transition.Timing.in}`};
    background: ${({ theme }) => theme.Color.System.Primary};
    border-color: ${({ theme }) => theme.Color.System.Primary};
    svg {
      fill: ${({ theme }) => theme.Color.Neutrals.Paper};
      transition: ${({ theme }) => `all ${theme.Transition.Timing.in}`}
    }
  }
`;
const AddButtonWrapper = styled.div`
  position: absolute;
  width: 50%;

  ${({ gripSize, type, position }) => css`
    ${type === 'row' && css`
      width: ${gripSize * 3}px;
      height: 50%;
      left: -${gripSize * 3}px;
    `}
    ${type === 'column' && css`
      top: -${gripSize * 3}px;
      width: 50%;
      height: ${gripSize * 3}px;
    `}


    ${type === 'row' && position === 'before' && css`
      top: 0;
    `}
    ${type === 'row' && position === 'after' && css`
      top: auto; bottom: 0;
    `}
    ${type === 'column' && position === 'before' && css`
      left: 0;
    `}
    ${type === 'column' && position === 'after' && css`
      left: auto; right: 0;
    `}
  `}
`;

const CloseIcon = (props) => (
  <CloseIconWrapper {...props}>
    <Icon
      color="transparent"
      name="cross"
      size="Small"
    />
  </CloseIconWrapper>
);

const AddIcon = (props) => {
  return (
  <AddButtonWrapper {...props}>
    <AddIconWrapper {...props}>
      <Icon
        color="transparent"
        name="editor-plus"
        size="Small"
        title="My Icon"
      />
    </AddIconWrapper>
  </AddButtonWrapper>
  );
};

export const StyledCellWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  ${({ width }) => width && css`
    width: ${width};
    background: red;
  `}
  ${({ height }) => height && css`
    height: ${height};
    background: blue;
  `}
`;

export const Cell = styled.div`
  ${cellStyle}
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

export function CellWrapper ({
  type,
  size,
  isActive,
  isOtherItemActive,
  gripSize,
  borderWidth,
  index,
  onClick,
  onRemove,
  onRemoveMouseEnter,
  onRemoveMouseLeave,
  onAddClick
}) {
  const [setHoverRef, edge] = useHoverEdgeDetection();
  const showHandleBefore = type === 'row'
    ? edge.vertical === 'top'
    : edge.horizontal === 'left';
  const showHandleAfter = type === 'row'
    ? edge.vertical === 'bottom'
    : edge.horizontal === 'right';

  return (
    <StyledCellWrapper ref={setHoverRef} gripSize={gripSize} {...size}>
      {!isActive && showHandleBefore && <AddIcon
        type={type}
        position={'before'}
        gripSize={gripSize}
        onClick={event => onAddClick(event, true, index)}
      />}
      {!isActive && showHandleAfter && <AddIcon
        type={type}
        position={'after'}
        gripSize={gripSize}
        onClick={event => onAddClick(event, false, index)}
      />}

      {(isActive && !isOtherItemActive) && <CloseIcon
        gripSize={gripSize}
        onClick={onRemove}
        onMouseEnter={(event) => onRemoveMouseEnter(event, type, index)}
        onMouseLeave={(event) => onRemoveMouseLeave(event, type, index)}
      />}

      <Cell
        isActive={isActive}
        borderWidth={borderWidth}
        gripSize={gripSize}
        onClick={onClick} />

    </StyledCellWrapper>
  );
}

export const RowWrapper = styled.div`
  display: flex;
  position: absolute;
  flex-direction: column;

  ${({ gripSize }) => css`
    top: ${gripSize}px;
    left: 0;
    width: ${gripSize}px;
    height: calc(100% - ${gripSize}px);
  `}

  ${StyledCellWrapper} {
    width: ${({ gripSize }) => gripSize}px;
    &:last-child {
      ${Cell} {
        border-bottom-width: ${({ borderWidth }) => borderWidth}px;
      }
    }
  }

  ${Cell} {
    border-left-width: ${({ borderWidth }) => borderWidth}px;
    border-top-width: ${({ borderWidth }) => borderWidth}px;
  }

  ${AddIconWrapper} {
    left: 0;
  }

  ${CloseIconWrapper} {
    ${({ gripSize }) => css`
      left: -${gripSize * 2}px;
    `}

    &:hover + ${Cell} {
      ${({ theme }) => css`
        background: ${theme.Color.Feedback.Alert};
        border-color: ${theme.Color.Feedback.Alert};
      `}
    }
  }
`;

export const ColumnWrapper = styled.div`
  display: flex;
  position: absolute;
  flex-direction: row;

  ${({ gripSize }) => css`
    top: 0;
    left: ${gripSize}px;
    width: calc(100% - ${gripSize}px);
    height: ${gripSize}px;
  `}

  ${StyledCellWrapper} {
    height: ${({ gripSize }) => gripSize}px;
    &:last-child {
      ${Cell} {
        border-right-width: ${({ borderWidth }) => borderWidth}px;
      }
    }
  }

  ${Cell} {
    border-left-width: ${({ borderWidth }) => borderWidth}px;
    border-top-width: ${({ borderWidth }) => borderWidth}px;
  }

  ${AddIconWrapper} {
    top: 0;
  }

  ${CloseIconWrapper} {
    ${({ gripSize }) => css`
      top: -${gripSize * 2}px;
    `}

    &:hover + ${Cell} {
      ${({ theme }) => css`
        background: ${theme.Color.Feedback.Alert};
        border-color: ${theme.Color.Feedback.Alert};
      `}
    }
  }
`;
